import { ReplaySubject, Subject } from "rxjs";
import { BirdBowlData } from "../types/bird/bird-bowl-data";
import { services, showMessage } from "../types/services";
import { UUID } from "../types/uuid";

export class InsightsService {
  public lastInsightsSubject: Subject<BirdBowlData[]> = new ReplaySubject(1);
  private lastInsights: BirdBowlData[] = [];
  public historicInsightsSubject: Subject<BirdBowlData[]> = new ReplaySubject(
    1
  );

  public getAllBirdDataForMatch(matchId: UUID): Promise<void> {
    const params: Map<string, string> = new Map();
    params.set("matchId", matchId.value);
    return services.httpService
      .get("/api/bird-scraper/get-for-match", params)
      .then((response) => {
        const allForMatch: BirdBowlData[] = response.map((update) =>
          this.deserializeBirdBowlData(update)
        );
        this.lastInsightsSubject.next(allForMatch);
        this.lastInsights = allForMatch;
      })
      .catch((reason) =>
        showMessage(`Failed to get all bird data: ${reason}`, "error")
      );
  }

  public geHistoricForGround(groundId: UUID): Promise<void> {
    return this.getHistoricForEntity(groundId, "ground");
  }

  public geHistoricForBatsman(batsmanId: UUID): Promise<void> {
    return this.getHistoricForEntity(batsmanId, "batsman");
  }

  public geHistoricForBowler(bowlerId: UUID): Promise<void> {
    return this.getHistoricForEntity(bowlerId, "bowler");
  }

  public testPython(): Promise<any> {
    return services.httpService.getFromPython("/insights/helloworld");
  }

  private getHistoricForEntity(
    entityId: UUID,
    type: "ground" | "bowler" | "batsman"
  ): Promise<void> {
    const params: Map<string, string> = new Map();
    params.set(type + "Id", entityId.value);
    return services.httpService
      .get(`/api/bird-scraper/get-historic-for-${type}`, params)
      .then((response) => {
        const historicForGround: BirdBowlData[] = response.map((update) =>
          this.deserializeBirdBowlData(update)
        );
        this.historicInsightsSubject.next(historicForGround);
      })
      .catch((reason) =>
        showMessage(
          `Failed to get historic bird data for ${type}: ${reason}`,
          "error"
        )
      );
  }

  public birdseyeUpdate(msg: any): void {
    const newValue = [...this.lastInsights];
    const update: BirdBowlData = this.deserializeBirdBowlData(msg.birdseyeInfo);
    const alreadyAdded: boolean =
      newValue.find(
        (birdBowlData) => birdBowlData.id.value === update.id.value
      ) !== undefined;

    if (alreadyAdded) {
      return;
    }

    newValue.push(update);
    this.lastInsightsSubject.next(newValue);
    this.lastInsights = newValue;
  }

  public deserializeBirdBowlData(update: any): BirdBowlData {
    return {
      id: update.id ? UUID.fromString(update.id) : null,
      sequence: update.sequence,
      birdMatchId: update.birdMatchId,
      year: update.year,
      month: update.month,
      day: update.day,
      bowlerId: update.bowlerId ? UUID.fromString(update.bowlerId) : null,
      batsmanId: update.batsmanId ? UUID.fromString(update.batsmanId) : null,
      innings: update.innings,
      over: update.over,
      ball: update.ball,
      bounceHeight: update.bounceHeight,
      deviation: update.deviation,
      swing: update.swing,
      slowDown: update.slowDown,
      xSlowDown: update.xSlowDown,
      zSlowDown: update.zSlowDown,
      bouncePosition: update.bouncePosition,
      adjustedBounceHeight: update.adjustedBounceHeight,
      expectation: update.expectation,
      runs: update.runs,
      dismissal: update.dismissal,
      bowlType: update.bowlType,
      longBowlTypes: update.longBowlTypes,
      bowlerName: update.bowlerName,
      noBall: update.noBall,
      wide: update.wide,
    };
  }
}
